import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  MessageSquare,
  Inbox,
  CheckCircle2,
  Clock,
  Plus,
  Trash2,
  CalendarClock,
  Pencil,
  RefreshCw,
  Trophy,
  FileText,
  Megaphone,
} from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const TYPE_ICON = {
  aviso: { icon: Megaphone, color: "bg-orange-100 text-orange-700" },
  status_change: { icon: RefreshCw, color: "bg-violet-100 text-[#7c3aed]" },
  budget_created: { icon: FileText, color: "bg-indigo-100 text-indigo-700" },
  admin_message: { icon: MessageSquare, color: "bg-sky-100 text-sky-700" },
  item_added: { icon: Plus, color: "bg-emerald-100 text-emerald-700" },
  item_updated: { icon: Pencil, color: "bg-amber-100 text-amber-700" },
  item_removed: { icon: Trash2, color: "bg-rose-100 text-rose-700" },
  item_completed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  item_reopened: { icon: Clock, color: "bg-sky-100 text-sky-700" },
  item_date_changed: { icon: CalendarClock, color: "bg-amber-100 text-amber-700" },
  concorrencias_available: { icon: Trophy, color: "bg-amber-100 text-amber-700" },
  obs: { icon: MessageSquare, color: "bg-violet-100 text-[#7c3aed]" },
};

export default function SchoolNotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [obs, setObs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get("/school/dashboard-stats");
      const c = (r.data?.unread_observations || 0) + (r.data?.unread_notifications || 0);
      setCount(c);
    } catch {}
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/school/notifications?limit=30"),
        api.get("/school/observations/unread"),
      ]);
      setNotifs(a.data || []);
      setObs(b.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 20000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  const goTo = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/school/notifications/${n.id}/read`); } catch {}
    }
    const link = n.link || (n.budget_id ? `/escola/orcamentos/${n.budget_id}` : "/escola");
    nav(link);
    setTimeout(loadCount, 500);
  };

  const goToObs = (o) => {
    setOpen(false);
    nav(`/escola/orcamentos/${o.budget_id}`);
    setTimeout(loadCount, 800);
  };

  const markAll = async () => {
    try {
      await api.post("/school/notifications/read-all");
      await loadItems();
      await loadCount();
      toast.success("Todas marcadas como lidas");
    } catch { toast.error("Falha ao atualizar"); }
  };

  // Build a unified list ordered by created_at desc
  const merged = [
    ...notifs.map((n) => ({ _kind: "n", ...n })),
    ...obs.map((o) => ({ _kind: "o", ...o })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2"
          data-testid="s-bell"
          aria-label="Notificações"
        >
          <Bell className="w-5 h-5 text-[#1e3a8a]" strokeWidth={2.2} />
          {count > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1"
              data-testid="s-bell-badge"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 max-w-[calc(100vw-24px)]" data-testid="s-notifications-popover">
        <div className="px-3 py-2.5 border-b border-slate-100 flex items-center justify-between gap-2">
          <div>
            <p className="font-heading text-sm font-black text-[#1e3a8a] flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notificações
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {count > 0 ? `${count} nova(s)` : "Tudo em dia"}
            </p>
          </div>
          {notifs.some((n) => !n.read) && (
            <button
              type="button"
              onClick={markAll}
              className="text-[11px] font-semibold text-[#7c3aed] hover:underline"
              data-testid="s-bell-mark-all"
            >
              Marcar todas lidas
            </button>
          )}
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {loading ? (
            <p className="text-xs text-slate-500 text-center py-6">Carregando...</p>
          ) : merged.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-500">Sem novidades por aqui.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {merged.map((n) => {
                const isObs = n._kind === "o";
                const key = isObs ? `obs-${n.id}` : `n-${n.id}`;
                const typeInfo = isObs ? TYPE_ICON.obs : (TYPE_ICON[n.type] || TYPE_ICON.status_change);
                const Icon = typeInfo.icon;
                const unread = isObs ? !n.read_by_school : !n.read;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => (isObs ? goToObs(n) : goTo(n))}
                      data-testid={`s-notif-item-${n.id}`}
                      className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex gap-2 ${unread ? "bg-violet-50/40" : ""}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${typeInfo.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-slate-900 line-clamp-1">
                          {isObs ? `AMC — ${n.author_name || "Mensagem"}` : n.title}
                        </p>
                        {n.budget_number && (
                          <p className="text-[11px] text-slate-500">
                            Orçamento Nº {n.budget_number}
                          </p>
                        )}
                        <p className="text-[12px] text-slate-700 line-clamp-2 mt-0.5">
                          {isObs ? n.text : n.body}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1">
                          {n.created_at ? new Date(n.created_at).toLocaleString("pt-BR") : ""}
                        </p>
                      </div>
                      {unread && (
                        <span className="w-2 h-2 rounded-full bg-[#7c3aed] mt-2 shrink-0" aria-label="não lida" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
